/**
 * Frontend-only mapping from `techniques_missed` strings (as emitted by the
 * supervisor LLM) to the Gottman chapter that covers the technique.
 *
 * The supervisor LLM emits techniques as descriptive sentences, not short
 * canonical tags — e.g. "Reflective listening or paraphrasing to demonstrate
 * understanding and build rapport". So we match by *substring*: every key
 * in the table is normalised once, then for each input we look for the
 * longest key that appears anywhere inside the normalised input. Longest-key
 * first lets "reflective listening" win over "listening" if both were keys.
 *
 * Normalisation:
 *   - lowercased
 *   - punctuation stripped (kept: a–z, 0–9, space, hyphen)
 *   - whitespace collapsed
 *
 * If no key matches, lookupBrushUp() returns null and the technique chip
 * renders plainly — we never fabricate a chapter.
 */

const TECHNIQUE_TO_CHAPTER = {
    "validation": { slug: "fondness-admiration", title: "Fondness & Admiration" },
    "reflective listening": { slug: "love-maps", title: "Love Maps" },
    "reflection of feelings": { slug: "love-maps", title: "Love Maps" },
    "reflections": { slug: "love-maps", title: "Love Maps" },
    "open questions": { slug: "love-maps", title: "Love Maps" },
    "open ended questions": { slug: "love-maps", title: "Love Maps" },
    "open-ended questions": { slug: "love-maps", title: "Love Maps" },
    "summarising": { slug: "love-maps", title: "Love Maps" },
    "summarizing": { slug: "love-maps", title: "Love Maps" },
    "paraphrasing": { slug: "love-maps", title: "Love Maps" },
    "turning towards": { slug: "turning-towards", title: "Turning Towards" },
    "bids for connection": { slug: "turning-towards", title: "Turning Towards" },
    "repair attempts": { slug: "managing-conflict", title: "Managing Conflict" },
    "gentle start-up": { slug: "managing-conflict", title: "Managing Conflict" },
    "gentle startup": { slug: "managing-conflict", title: "Managing Conflict" },
    "softened start-up": { slug: "managing-conflict", title: "Managing Conflict" },
    "softened startup": { slug: "managing-conflict", title: "Managing Conflict" },
    "physiological self-soothing": { slug: "managing-conflict", title: "Managing Conflict" },
    "physiological self soothing": { slug: "managing-conflict", title: "Managing Conflict" },
    "antidotes to the four horsemen": { slug: "managing-conflict", title: "Managing Conflict" },
    "four horsemen": { slug: "managing-conflict", title: "Managing Conflict" },
    "fondness and admiration": { slug: "fondness-admiration", title: "Fondness & Admiration" },
    "appreciations": { slug: "fondness-admiration", title: "Fondness & Admiration" },
    "positive sentiment override": { slug: "positive-perspective", title: "The Positive Perspective" },
    "magic ratio": { slug: "positive-perspective", title: "The Positive Perspective" },
    "shared meaning": { slug: "sound-relationship-house", title: "The Sound Relationship House" },
    "love maps": { slug: "love-maps", title: "Love Maps" },
};

const normalise = (s) =>
    String(s || "")
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, "")
        .replace(/\s+/g, " ")
        .trim();

// Pre-sort keys by length desc so the longest match wins ("reflective listening"
// beats "listening", "open-ended questions" beats "open questions", etc.).
const SORTED_KEYS = Object.keys(TECHNIQUE_TO_CHAPTER).sort((a, b) => b.length - a.length);

export const lookupBrushUp = (technique) => {
    const key = normalise(technique);
    if (!key) return null;
    // Exact match is the cheapest happy path; otherwise scan keys for a substring.
    if (TECHNIQUE_TO_CHAPTER[key]) return TECHNIQUE_TO_CHAPTER[key];
    for (const k of SORTED_KEYS) {
        if (key.includes(k)) return TECHNIQUE_TO_CHAPTER[k];
    }
    return null;
};

// Exposed for tests / sanity checks.
export const __TECHNIQUE_TO_CHAPTER = TECHNIQUE_TO_CHAPTER;
